import React from 'react';
import { ScheduleMarker } from '../Scheduling/ScheduledSettings';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Link as MuiLink,
  TextField,
  Typography
} from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InfoButton } from '../../FormWidgets/Description';
import { EditableLabel } from '../../FormWidgets/EditableLabel';
import MuiSwitch from '../../FormWidgets/SettingsSwitch';
import { ShowSetting } from '../../FormWidgets/ShowSliderSetting';
import useLightStyles from '../../components/lightChannelStyles';
import LightCurveEditor from '../../components/LightCurveEditor';
import SimpleLightCurve from '../../components/SimpleLightCurve';
import LightProfilesOverview from '../../components/LightProfilesOverview';
import IndexedSettingsTabs from '../../components/IndexedSettingsTabs';
import { resolveLightProfile } from '../../Utils/light_profile_source';
import {
  channelKey,
  channelLabel,
  formatLightMinute,
  LIGHT_LIMITS
} from '../../Utils/light_profiles';

// Shared lighting presentation. Its owner supplies a draft and edit callback;
// settings transport, confirmation and persistence remain with the page adapter.
export default function LightingSection({
  controller,
  catalog,
  ordinaryCatalog = catalog,
  baseline,
  ordinaryBaseline = baseline,
  zone,
  cycle,
  writable,
  runtime,
  sunriseDuration = 0,
  subzoneCount = 4,
  onChange: edit,
  onCreateShared: createShared,
  onActiveSubzoneChange,
  feedback,
  footer,
  phaseControls,
  scheduledSetting,
  scheduledControls,
  profilePointCount,
  idPrefix = 'light'
}) {
  const { t } = useTranslation();
  const classes = useLightStyles();
  const advanced = Boolean(catalog.advanced_zone_mask & (1 << zone));
  const channels = catalog.channels.filter((channel) => channel.zone === zone);
  const zoneSettings = catalog.zones.find((entry) => entry.zone === zone);
  const savedZoneSettings = baseline.zones.find((entry) => entry.zone === zone);
  const shared = Boolean(zoneSettings.shared_toplight);
  const sharedChannel = channels.find((channel) => channel.subzone === 0 && channel.channel === 0);
  const sharedSubzone = catalog.subzones.find(
    (entry) => entry.zone === zone && entry.subzone === 0
  );
  const pointCount =
    profilePointCount ??
    ordinaryCatalog.channels.reduce((count, channel) => count + channel.points.length, 0);
  const fieldDisabled = (field, identity) =>
    scheduledSetting?.(field, identity) ? !scheduledControls?.writable :
      !writable || Boolean(phaseControls && !phaseControls.selected(field, identity));
  const fieldResetDisabled = (field, identity) => Boolean(
    scheduledSetting?.(field, identity) && !scheduledControls?.canReset(field, identity));
  const resetField = (field, identity, reset) => {
    if (!fieldResetDisabled(field, identity)) reset();
  };
  const scheduledChange = (field, identity, value, saveTemp) => {
    if (!scheduledSetting?.(field, identity)) return false;
    scheduledControls?.change(field, identity, value, saveTemp);
    return true;
  };
  const renderOverride = (field, identity) =>
    phaseControls ? (
      <FormControlLabel
        label={t(
          field === 'mode'
            ? 'lighting_override_mode_label'
            : field.endsWith('_master')
              ? 'lighting_override_master_label'
              : field.endsWith('_enabled')
                ? 'lighting_enabled'
                : field === 'channel_maximum'
                  ? 'lighting_override_maximum_label'
                  : 'lighting_override_profile_label'
        )}
        control={
          <Checkbox
            inputProps={{
              'aria-label': t('lighting_schedule_setting', {
                setting:
                  field === 'mode'
                    ? t('lighting_mode_help')
                    : field.startsWith('zone_')
                      ? t(
                          field === 'zone_master' ? 'lighting_zone_master' : 'lighting_zone_enabled'
                        )
                      : field.startsWith('subzone_')
                        ? `${t('lighting_subzone')} ${identity.subzone + 1} / ${t(field === 'subzone_master' ? 'lighting_master' : 'lighting_enabled')}`
                        : `${channelLabel(identity)} / ${t(field === 'channel_maximum' ? 'lighting_phase_maximum' : field === 'channel_enabled' ? 'lighting_enabled' : 'lighting_channel_profile')}`
              })
            }}
            color="primary"
            checked={phaseControls.selected(field, identity)}
            disabled={!writable || (field === 'channel_profile' && identity._profile_off)}
            onChange={(_, selected) => phaseControls.select(field, identity, selected)}
          />
        }
      />
    ) : (
      <ScheduleMarker target={scheduledSetting?.(field, identity)} />
    );
  const setZone = (patch, saveTemp = true) => {
    if (phaseControls) {
      Object.entries(patch).forEach(([field, value]) =>
        phaseControls.change(`zone_${field}`, null, value, saveTemp)
      );
      return;
    }
    const ordinaryPatch = Object.fromEntries(Object.entries(patch).filter(([field, value]) =>
      !scheduledChange(`zone_${field}`, null, value, saveTemp)));
    if (!Object.keys(ordinaryPatch).length) return;
    edit(
      {
        ...ordinaryCatalog,
        zones: ordinaryCatalog.zones.map((entry) => (entry.zone === zone ? { ...entry, ...ordinaryPatch } : entry))
      },
      saveTemp
    );
  };
  const setChannel = (identity, patch, saveTemp = true) => {
    if (phaseControls) {
      const channel = channels.find((entry) => channelKey(entry) === identity);
      Object.entries(patch).forEach(([field, value]) =>
        phaseControls.change(
          field === 'points' ? 'channel_profile' : `channel_${field}`,
          channel,
          value,
          saveTemp
        )
      );
      return;
    }
    const channel = channels.find((entry) => channelKey(entry) === identity);
    const ordinaryPatch = Object.fromEntries(Object.entries(patch).filter(([field, value]) =>
      !scheduledChange(field === 'points' ? 'channel_profile' : `channel_${field}`, channel, value, saveTemp)));
    if (!Object.keys(ordinaryPatch).length) return;
    edit(
      {
        ...ordinaryCatalog,
        channels: ordinaryCatalog.channels.map((channel) =>
          channelKey(channel) === identity ? { ...channel, ...ordinaryPatch } : channel
        )
      },
      saveTemp
    );
  };
  const setSubzone = (subzone, patch, saveTemp = true) => {
    if (phaseControls) {
      Object.entries(patch).forEach(([field, value]) =>
        phaseControls.change(`subzone_${field}`, { subzone }, value, saveTemp)
      );
      return;
    }
    const ordinaryPatch = Object.fromEntries(Object.entries(patch).filter(([field, value]) =>
      !scheduledChange(`subzone_${field}`, { subzone }, value, saveTemp)));
    if (!Object.keys(ordinaryPatch).length) return;
    edit(
      {
        ...ordinaryCatalog,
        subzones: ordinaryCatalog.subzones.map((entry) =>
          entry.zone === zone && entry.subzone === subzone ? { ...entry, ...ordinaryPatch } : entry
        )
      },
      saveTemp
    );
  };
  const savedOrdinaryLimits = (c) => {
    const saved = ordinaryBaseline.channels.find(
      (channel) => channelKey(channel) === channelKey(c)
    );
    return { minimum: saved?.minimum ?? 10, maximum: saved?.maximum ?? 100 };
  };
  const resetOrdinaryLimits = (c) => edit({
    ...ordinaryCatalog,
    channels: ordinaryCatalog.channels.map((channel) =>
      channelKey(channel) === channelKey(c) ? { ...channel, ...savedOrdinaryLimits(c) } : channel
    )
  });
  const renderChannelCutoff = (c) => {
    const ordinary = ordinaryCatalog.channels.find(
      (channel) => channelKey(channel) === channelKey(c)
    );
    return (
      <ShowSetting
        id={`${idPrefix}_channel_${c.subzone}_${c.channel}_minimum`}
        show="lighting_cutoff_label"
        controller={controller}
        cur_value={ordinary.minimum}
        controller_value={savedOrdinaryLimits(c).minimum}
        min={0}
        max={ordinary.maximum}
        unit="%"
        disabled={!writable}
        controls={{
          setValue: (_, minimum, saveTemp) => setChannel(channelKey(c), { minimum }, saveTemp),
          resetSetting: () => resetOrdinaryLimits(c)
        }}
        helpOverride={{
          brief: t('lighting_limits_help'),
          content: `${t('lighting_cutoff_description')}\n\n${t('lighting_limit_reset_help')}`
        }}
      />
    );
  };
  const renderChannelMaximum = (c) => {
    const phaseMaximum = Boolean(phaseControls || scheduledSetting?.('channel_maximum', c));
    return (
      <ShowSetting
        id={`${idPrefix}_channel_${c.subzone}_${c.channel}_maximum`}
        show={phaseControls ? 'lighting_phase_maximum' : 'lighting_override_maximum_label'}
        controller={controller}
        cur_value={c.maximum}
        controller_value={
          phaseMaximum
            ? baseline.channels.find((channel) => channelKey(channel) === channelKey(c))?.maximum ??
              100
            : savedOrdinaryLimits(c).maximum
        }
        min={phaseMaximum ? 0 : c.minimum}
        max={115}
        unit="%"
        disabled={fieldDisabled('channel_maximum', c)}
        resetDisabled={fieldResetDisabled('channel_maximum', c)}
        controls={{
          setValue: (_, maximum, saveTemp) => setChannel(channelKey(c), { maximum }, saveTemp),
          resetSetting: () =>
            resetField('channel_maximum', c, () => {
              if (!phaseMaximum) resetOrdinaryLimits(c);
              else
                setChannel(channelKey(c), {
                  maximum:
                    baseline.channels.find((channel) => channelKey(channel) === channelKey(c))
                      ?.maximum ?? 100
                });
            })
        }}
        valueBoxStyle={{ whiteSpace: 'normal' }}
        valueActions={!phaseControls && renderOverride('channel_maximum', c)}
        helpOverride={{
          brief: t('lighting_limits_help'),
          content: `${t('lighting_cutoff_description')}${phaseMaximum ? '' : `\n\n${t('lighting_limit_reset_help')}`}`
        }}
      >
        {phaseControls && renderOverride('channel_maximum', c)}
      </ShowSetting>
    );
  };
  const renderChannelControls = (c, subzone) => (
    <>
      <Box className={classes.fields}>
        {phaseControls ? (
          <Typography>{channelLabel(c)}</Typography>
        ) : (
          <EditableLabel
            id={`light-channel-${channelKey(c)}`}
            label={t('lighting_channel_name_label', { number: c.channel + 1 })}
            value={c.name}
            placeholder={channelLabel(c)}
            dialogTitle={t('lighting_rename_channel')}
            inputLabel={t('lighting_channel_name_label', { number: c.channel + 1 })}
            validate={(name) => (new Blob([name]).size > 20 ? 'lighting_name_limit' : '')}
            disabled={!writable}
            handleInput={(_, __, name) =>
              setChannel(channelKey(c), { name: name === -1 ? '' : name })
            }
          />
        )}
        {renderOverride('channel_enabled', c)}
        <FormControlLabel
          label={t('lighting_enabled', { defaultValue: 'Enabled' })}
          control={
            <MuiSwitch
              color="primary"
              disabled={fieldDisabled('channel_enabled', c)}
              checked={Boolean(subzone.enabled_mask & (1 << c.channel))}
              controller_value={Boolean(
                (baseline.subzones.find((s) => s.zone === zone && s.subzone === c.subzone)
                  ?.enabled_mask ?? 0) &
                  (1 << c.channel)
              )}
              onChange={(_, checked) =>
                phaseControls
                  ? phaseControls.change('channel_enabled', c, checked)
                  : (() => {
                      if (scheduledChange('channel_enabled', c, checked)) return;
                      const ordinary = ordinaryCatalog.subzones.find(
                        (entry) => entry.zone === zone && entry.subzone === subzone.subzone
                      );
                      setSubzone(subzone.subzone, {
                        enabled_mask: checked
                          ? ordinary.enabled_mask | (1 << c.channel)
                          : ordinary.enabled_mask & ~(1 << c.channel)
                      });
                    })()
              }
            />
          }
        />
      </Box>
      {renderChannelMaximum(c)}
    </>
  );
  const renderChannelSetup = (c, sharedCard = false) => (
    <Box key={c.channel} mt={2} className={classes.card}>
      <Typography>{channelLabel(c)}</Typography>
      {renderChannelCutoff(c)}
      {advanced && !sharedCard && (
        <Button
          disabled={!writable}
          onClick={() => {
            const remaining = ordinaryCatalog.channels.filter(
              (item) => channelKey(item) !== channelKey(c)
            );
            edit({
              ...ordinaryCatalog,
              channels: remaining,
              subzones: ordinaryCatalog.subzones.map((s) =>
                s.zone === zone && s.subzone === c.subzone
                  ? {
                      ...s,
                      enabled_mask: s.enabled_mask & ~(1 << c.channel),
                      visible_mask: s.visible_mask & ~(1 << c.channel),
                      main_channel: s.main_channel === c.channel ? 255 : s.main_channel
                    }
                  : s
              )
            });
          }}
        >
          {t('lighting_delete_channel', { defaultValue: 'Delete channel and profile' })}
        </Button>
      )}
    </Box>
  );
  const renderSubzoneEnable = (subzone) => (
    <>
      {phaseControls && renderOverride('subzone_enabled', subzone)}
      <FormControlLabel
        label={t('lighting_subzone_enabled', { number: subzone.subzone + 1 })}
        control={
          <MuiSwitch
            color="primary"
            disabled={fieldDisabled('subzone_enabled', subzone)}
            checked={Boolean(subzone.enabled)}
            controller_value={Boolean(
              baseline.subzones.find(
                (entry) => entry.zone === zone && entry.subzone === subzone.subzone
              )?.enabled
            )}
            onChange={(_, checked) => setSubzone(subzone.subzone, { enabled: checked ? 1 : 0 })}
          />
        }
      />
    </>
  );
  const renderInheritedPreview = (channel, subzone) => {
    const inherited = resolveLightProfile(channel, channels, catalog.subzones, zoneSettings);
    const isShared = shared && channel.subzone === 0 && channel.channel === 0;
    return (
      <Box>
        {inherited.source && inherited.source !== channel && (
          <Typography variant="body2">
            {t('lighting_follows_main', { name: channelLabel(inherited.source) })}
          </Typography>
        )}
        <SimpleLightCurve
          cycle={cycle}
          channel={channel}
          enabled={Boolean(
            inherited.kind !== 'off' &&
              zoneSettings.enabled &&
              (isShared || subzone.enabled) &&
              subzone.enabled_mask & (1 << channel.channel)
          )}
          profilePoints={inherited.kind === 'custom' ? inherited.points : undefined}
          zoneMaster={zoneSettings.master}
          subzoneMaster={isShared ? 100 : subzone.master}
          title={t(
            inherited.kind === 'analytic' ? 'lighting_simple_curve' : 'lighting_configured_output'
          )}
          sunriseDuration={sunriseDuration}
        />
      </Box>
    );
  };
  const renderProfile = (channel, subzone) => (
    <>
      {renderOverride('channel_profile', channel)}
      {phaseControls && channel._profile_off && (
        <Typography variant="body2">{t('lighting_phase_enable_before_profile')}</Typography>
      )}
      <LightCurveEditor
        profileKey={`${idPrefix}:${controller.id}:${channelKey(channel)}`}
        points={phaseControls ? phaseControls.points(channel) : channel.points}
        cycle={cycle}
        availablePoints={LIGHT_LIMITS.points - (scheduledSetting?.('channel_profile', channel)
          ? scheduledControls.profilePointCount : pointCount)}
        disabled={fieldDisabled('channel_profile', channel)}
        onChange={(points) => setChannel(channelKey(channel), { points })}
        emptyPreview={renderInheritedPreview(channel, subzone)}
      />
    </>
  );
  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.heading}>
          {t('lighting_title', {
            defaultValue: 'Light channels'
          })}
          <InfoButton
            brief={t('lighting_mode_help')}
            content={`${t('lighting_simple_description')}\n\n${t('lighting_advanced_description')}`}
          />
        </Typography>
        <Box display="flex" alignItems="center">
          {renderOverride('mode')}
          <Typography>
            {t('lighting_simple', {
              defaultValue: 'Simple'
            })}
          </Typography>
          <MuiSwitch
            color="primary"
            checked={advanced}
            controller_value={Boolean(baseline.advanced_zone_mask & (1 << zone))}
            disabled={fieldDisabled('mode')}
            onChange={(_, checked) =>
              phaseControls
                ? phaseControls.change('mode', null, checked)
                : !scheduledChange('mode', null, checked) && edit({
                    ...ordinaryCatalog,
                    advanced_zone_mask: checked
                      ? ordinaryCatalog.advanced_zone_mask | (1 << zone)
                      : ordinaryCatalog.advanced_zone_mask & ~(1 << zone)
                  })
            }
            inputProps={{
              'aria-label': 'Advanced lighting mode'
            }}
          />
          <Typography>
            {t('lighting_advanced', {
              defaultValue: 'Advanced'
            })}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.meta}>
        <Typography variant="body2" color="textSecondary">
          {t('lighting_day')} {formatLightMinute(cycle.start)} {'\u2013'}{' '}
          {formatLightMinute(cycle.end)}
          {!phaseControls && (
            <>
              {' \u00b7 '}
              <MuiLink
                component={Link}
                to={`/controller/${controller.id}/zones/zone_${zone}/timing`}
              >
                {t('lighting_edit_time')}
              </MuiLink>
            </>
          )}
        </Typography>
      </Box>
      {feedback}
      <Box component="section" aria-label={t('lighting_zone_master')}>
        <ShowSetting
          id={`${idPrefix}_zone_${zone}_master`}
          show="lighting_zone_master"
          controller={controller}
          cur_value={zoneSettings.master}
          controller_value={savedZoneSettings.master}
          min={0}
          max={100}
          unit="%"
          disabled={fieldDisabled('zone_master')}
          resetDisabled={fieldResetDisabled('zone_master')}
          controls={{
            setValue: (_, master, saveTemp) => setZone({ master }, saveTemp),
            resetSetting: () => resetField('zone_master', undefined, () => setZone({ master: savedZoneSettings.master }))
          }}
          valueBoxStyle={{ whiteSpace: 'normal', marginBottom: 16 }}
          valueActions={!phaseControls && <ScheduleMarker target={scheduledSetting?.('zone_master') || scheduledSetting?.('zone_enabled')} />}
          helpOverride={{ brief: t('lighting_zone_master'), content: t('lighting_zone_master_help') }}
        >
          {phaseControls && renderOverride('zone_master')}
          {phaseControls && renderOverride('zone_enabled')}
          <FormControlLabel
            label={t('lighting_zone_enabled')}
            control={
              <MuiSwitch
                color="primary"
                disabled={fieldDisabled('zone_enabled')}
                checked={Boolean(zoneSettings.enabled)}
                controller_value={Boolean(savedZoneSettings.enabled)}
                onChange={(_, checked) => setZone({ enabled: checked ? 1 : 0 })}
              />
            }
          />
        </ShowSetting>
      </Box>
      {!phaseControls && (
        <Box className={classes.actions}>
          <FormControlLabel
            label={t('lighting_shared_toplight')}
            control={
              <MuiSwitch
                color="primary"
                disabled={!writable || !sharedChannel}
                checked={shared}
                controller_value={Boolean(savedZoneSettings.shared_toplight)}
                onChange={(_, checked) => setZone({ shared_toplight: checked ? 1 : 0 })}
              />
            }
          />
          <InfoButton
            brief={t('lighting_shared_toplight')}
            content={t('lighting_shared_toplight_help')}
          />
          {!sharedChannel && (
            <Button
              color="primary"
              variant="outlined"
              disabled={
                !writable ||
                catalog.channels.length >= LIGHT_LIMITS.channels ||
                channels.length >= LIGHT_LIMITS.zoneChannels
              }
              onClick={createShared}
            >
              {t('lighting_create_shared_toplight')}
            </Button>
          )}
        </Box>
      )}
      {shared && sharedChannel && (
        <Box
          component="section"
          aria-label={t('lighting_shared_toplight')}
          className={classes.card}
        >
          <Typography variant="h6">{t('lighting_shared_toplight')}</Typography>
          {renderChannelControls(sharedChannel, sharedSubzone)}
          {!advanced && (
            <SimpleLightCurve
              cycle={cycle}
              channel={sharedChannel}
              enabled={Boolean(zoneSettings.enabled && sharedSubzone.enabled_mask & 1)}
              zoneMaster={zoneSettings.master}
              sunriseDuration={sunriseDuration}
            />
          )}
          {advanced && renderProfile(sharedChannel, sharedSubzone)}
          {!phaseControls && (
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t('lighting_installation')}
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                {renderChannelSetup(sharedChannel, true)}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}
      {(advanced || !shared) && (
        <IndexedSettingsTabs
          idPrefix={`${idPrefix}-subzone`}
          contained
          onActiveChange={onActiveSubzoneChange}
          items={catalog.subzones
            .filter((s) => s.zone === zone && s.subzone < subzoneCount)
            .map((s) => ({
              ...s,
              id: s.subzone,
              label: `${t('lighting_subzone')} ${s.subzone + 1}`
            }))}
          renderPanel={(subzone) => {
            const allocatedMembers = channels.filter((c) => c.subzone === subzone.subzone);
            const members = allocatedMembers.filter(
              (c) => !(shared && c.subzone === 0 && c.channel === 0)
            );
            const main = members.find((c) => c.channel === subzone.main_channel);
            const visibleMembers = advanced ? members : members.filter((c) => c === main);
            return (
              <Box key={subzone.subzone} mt={3}>
                {
                  <ShowSetting
                    id={`${idPrefix}_subzone_${subzone.subzone}_master`}
                    show="lighting_master"
                    controller={controller}
                    cur_value={subzone.master}
                    controller_value={
                      baseline.subzones.find(
                        (s) => s.zone === zone && s.subzone === subzone.subzone
                      )?.master ?? 100
                    }
                    min={0}
                    max={100}
                    unit="%"
                    disabled={fieldDisabled('subzone_master', subzone)}
                    resetDisabled={fieldResetDisabled('subzone_master', subzone)}
                    controls={{
                      setValue: (_, master, saveTemp) =>
                        setSubzone(subzone.subzone, { master }, saveTemp),
                      resetSetting: () => resetField('subzone_master', subzone, () =>
                        setSubzone(subzone.subzone, {
                          master:
                            baseline.subzones.find(
                              (s) => s.zone === zone && s.subzone === subzone.subzone
                            )?.master ?? 100
                        }))
                    }}
                    valueBoxStyle={{ whiteSpace: 'normal', marginBottom: 16 }}
                    valueActions={!phaseControls && <ScheduleMarker target={scheduledSetting?.('subzone_master', subzone) || scheduledSetting?.('subzone_enabled', subzone)} />}
                    helpOverride={{ brief: t('lighting_master_help'), content: t('lighting_master_description') }}
                  >
                    {phaseControls && renderOverride('subzone_master', subzone)}
                    {renderSubzoneEnable(subzone)}
                  </ShowSetting>
                }
                {!advanced && main && (
                  <Box className={classes.card}>
                    {renderChannelControls(main, subzone)}
                    <SimpleLightCurve
                      cycle={cycle}
                      channel={main}
                      enabled={Boolean(
                        zoneSettings.enabled &&
                          subzone.enabled &&
                          main &&
                          subzone.enabled_mask & (1 << main.channel)
                      )}
                      zoneMaster={zoneSettings.master}
                      subzoneMaster={subzone.master}
                      sunriseDuration={sunriseDuration}
                    />
                  </Box>
                )}
                {advanced &&
                  members.map((c) => {
                    const status = runtime?.channels?.find((r) => channelKey(r) === channelKey(c));
                    return (
                      <Accordion className={classes.accordion} key={c.channel}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          {channelLabel(c)}
                          {!(subzone.enabled_mask & (1 << c.channel))
                            ? ` (${t('lighting_disabled')})`
                            : ''}
                        </AccordionSummary>
                        <AccordionDetails className={classes.details}>
                          {renderChannelControls(c, subzone)}
                          {status && (
                            <Typography>
                              {t('lighting_requested', {
                                defaultValue: 'Requested'
                              })}
                              {status.requested / 10}
                              {t('lighting_actual', {
                                defaultValue: '% / actual'
                              })}
                              {status.actual / 10}%
                              {status.flags & 8 ? ` | ${t('lighting_thermal_limitation')}` : ''}
                              {status.flags & 4 ? ` | ${t('lighting_manual_override')}` : ''}
                              {status.flags & ~63 ? ` | ${t('unknown')}` : ''}
                            </Typography>
                          )}
                          {renderProfile(c, subzone)}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                {!phaseControls && (
                  <Accordion className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      {t('lighting_installation')}
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                      <Box className={classes.fields}>
                        <TextField
                          id={`${idPrefix}_subzone_${subzone.subzone}_main_channel`}
                          fullWidth
                          select
                          label={t('lighting_main_channel_label', {
                            number: subzone.subzone + 1
                          })}
                          value={subzone.main_channel}
                          disabled={!writable}
                          onChange={(e) =>
                            setSubzone(subzone.subzone, {
                              main_channel: Number(e.target.value)
                            })
                          }
                        >
                          <MenuItem value={255}>
                            {t('lighting_none', {
                              defaultValue: 'None'
                            })}
                          </MenuItem>
                          {shared && subzone.subzone === 0 && subzone.main_channel === 0 && (
                            <MenuItem value={0} disabled>
                              {t('lighting_shared_toplight')}
                            </MenuItem>
                          )}
                          {members.map((c) => (
                            <MenuItem key={c.channel} value={c.channel}>
                              {channelLabel(c)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <InfoButton
                          brief={t('lighting_installation_help')}
                          content={t('lighting_setup_help')}
                        />
                      </Box>
                      {visibleMembers.map((c) => renderChannelSetup(c))}
                      {advanced && (
                        <Button
                          disabled={
                            !writable ||
                            allocatedMembers.length >= 6 ||
                            catalog.channels.length >= LIGHT_LIMITS.channels
                          }
                          onClick={() => {
                            const channel = Array.from(
                              {
                                length: 6
                              },
                              (_, i) => i
                            ).find((id) => !allocatedMembers.some((c) => c.channel === id));
                            edit({
                              ...ordinaryCatalog,
                              channels: [
                                ...ordinaryCatalog.channels,
                                {
                                  zone,
                                  subzone: subzone.subzone,
                                  channel,
                                  name: '',
                                  minimum: 10,
                                  maximum: 100,
                                  points: []
                                }
                              ],
                              subzones: ordinaryCatalog.subzones.map((s) =>
                                s.zone === zone && s.subzone === subzone.subzone
                                  ? {
                                      ...s,
                                      enabled_mask: s.enabled_mask | (1 << channel),
                                      visible_mask: s.visible_mask | (1 << channel)
                                    }
                                  : s
                              )
                            });
                          }}
                        >
                          {t('lighting_add_channel', {
                            defaultValue: 'Add channel'
                          })}
                        </Button>
                      )}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            );
          }}
        />
      )}
      {advanced && (
        <Accordion className={classes.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('lighting_overview_title')}
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <LightProfilesOverview
              controllerId={controller.id}
              zoneSettings={zoneSettings}
              sunriseDuration={sunriseDuration}
              channels={channels}
              subzones={catalog.subzones.filter((s) => s.zone === zone)}
              cycle={cycle}
            />
          </AccordionDetails>
        </Accordion>
      )}
      {footer}
    </Box>
  );
}
